// src/ui/components/pwaInstallPrompt.ts — PWA Installation Banner (T-9.1.4, NFR-PWA-001, CON-004)
// Catches beforeinstallprompt event and renders a tailored tablet home-screen install card.

import { tokens } from '../designTokens';
import { soundEngine } from '@audio/soundEngine';

const PWA_DISMISSED_KEY = 'clawey_pwa_dismissed_until';

export class PwaInstallPrompt {
  private container: HTMLElement;
  private deferredPrompt: any = null;
  private isVisible: boolean = false;
  private onInstalled?: () => void;

  constructor(options: { onInstalled?: () => void } = {}) {
    this.container = document.createElement('div');
    this.container.id = 'pwa-install-prompt-root';
    this.container.className = 'pwa-install-prompt';
    this.onInstalled = options.onInstalled;

    this.init();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  getIsVisible(): boolean {
    return this.isVisible;
  }

  private init(): void {
    if (typeof window === 'undefined') return;

    // Check if dismissed recently
    const dismissedUntil = localStorage.getItem(PWA_DISMISSED_KEY);
    if (dismissedUntil && Date.now() < Number(dismissedUntil)) {
      return;
    }

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.show();
    });

    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.hide();
      soundEngine.playSuccess();
      if (this.onInstalled) {
        this.onInstalled();
      }
    });
  }

  show(): void {
    this.isVisible = true;
    this.render();
  }

  hide(): void {
    this.isVisible = false;
    this.container.innerHTML = '';
    this.container.style.display = 'none';
  }

  dismissForWeek(): void {
    // Dismiss for 7 days
    const nextWeek = Date.now() + 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem(PWA_DISMISSED_KEY, String(nextWeek));
    this.hide();
  }

  async promptInstall(): Promise<boolean> {
    if (!this.deferredPrompt) {
      return false;
    }
    soundEngine.playTap();
    this.deferredPrompt.prompt();
    const { outcome } = await this.deferredPrompt.userChoice;
    this.deferredPrompt = null;
    this.hide();
    return outcome === 'accepted';
  }

  render(): HTMLElement {
    if (!this.isVisible) {
      this.container.innerHTML = '';
      this.container.style.display = 'none';
      return this.container;
    }

    this.container.style.display = 'block';
    this.container.innerHTML = `
      <aside
        id="pwa-install-banner"
        role="region"
        aria-label="App Installation"
        style="
          position: fixed;
          bottom: 20px;
          right: 20px;
          max-width: 420px;
          background: #FFFDF9;
          border: 2px solid ${tokens.color.gildedBrass};
          border-radius: 18px;
          padding: 20px;
          box-shadow: 0 10px 32px rgba(7, 20, 36, 0.22);
          z-index: 990;
          animation: slideUpInstall 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        "
      >
        <style>
          @keyframes slideUpInstall {
            from { transform: translateY(30px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        </style>

        <div style="display: flex; gap: 14px; align-items: flex-start; margin-bottom: 14px;">
          <img
            src="/assets/svg/clawey-icon-192.svg"
            alt="Clawey Lobster Icon"
            style="width: 48px; height: 48px; flex-shrink: 0;"
          />
          <div>
            <div style="font-size: 0.75rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
              Install PWA on Tablet
            </div>
            <h4 style="font-family: ${tokens.font.heading}; font-size: 1.15rem; color: ${tokens.color.morrisIndigo}; margin: 2px 0 4px;">
              Install Clawey on Tudor’s Device
            </h4>
            <p style="font-size: 0.85rem; color: #5A6D86; margin: 0; line-height: 1.45;">
              Fast full-screen study mode, offline practice, and zero browser distractions.
            </p>
          </div>
        </div>

        <div style="display: flex; gap: 10px; justify-content: flex-end; align-items: center;">
          <button
            id="btn-pwa-dismiss"
            type="button"
            class="touch-target"
            style="
              padding: 10px 16px;
              font-size: 0.85rem;
              font-weight: 700;
              color: #5A6D86;
              background: transparent;
              border: 1px solid rgba(26, 54, 93, 0.2);
              border-radius: 10px;
              cursor: pointer;
              min-height: 48px;
            "
          >
            Not Now
          </button>
          <button
            id="btn-pwa-install"
            type="button"
            class="btn-primary touch-target"
            style="
              padding: 10px 20px;
              font-size: 0.9rem;
              font-weight: 800;
              border-radius: 10px;
              min-height: 48px;
            "
          >
            📥 Install App &rarr;
          </button>
        </div>
      </aside>
    `;

    this.attachEventListeners();
    return this.container;
  }

  private attachEventListeners(): void {
    const installBtn = this.container.querySelector<HTMLButtonElement>('#btn-pwa-install');
    installBtn?.addEventListener('click', () => {
      this.promptInstall();
    });

    const dismissBtn = this.container.querySelector<HTMLButtonElement>('#btn-pwa-dismiss');
    dismissBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.dismissForWeek();
    });
  }
}
